/**
 * TEMP-H-001: API Types for Frontend Integration
 * 
 * Typed contracts for the four new backend endpoints that provide
 * single source of truth for settings, thresholds, iterations, and QC data.
 */

import type { StageType } from '@/types/execution';

// Stage name normalization utility
export function normalizeStage(stage: string): StageType {
  // Handle legacy 'video' -> 'videos' mapping
  return (stage === 'video' ? 'videos' : stage) as StageType;
}

// GET /api/executions/:executionId/settings
export interface ExecutionSettings {
  executionId: string;
  version: string;
  stages: StageSettings[];
}

export interface StageSettings {
  stage: StageType;
  qcPersonaSlug: string;
  passMin: number;
  fallbackMin: number;
  retryLimit: number;
  categoryThresholds: Record<string, number>;
  isDefaultFromLaunch: boolean;
  updatedAt: string;
}

// GET /api/executions/:executionId/stages/:stage/thresholds
export interface StageThresholds {
  executionId: string;
  stage: StageType;
  version: string;
  qcPersonaSlug: string;
  passMin: number;
  fallbackMin: number;
  retryLimit: number;
  categoryThresholds: Record<string, number>;
  updatedAt: string;
}

// GET /api/executions/:executionId/stages/:stage/iterations
export interface StageIterations {
  executionId: string;
  stage: StageType;
  items: IterationItem[];
}

export interface IterationItem {
  iterationNumber: number;
  accepted: boolean;
  createdAt: string;
  costUsd?: number;
  modelUsed?: string;
  artifacts: ArtifactRef[];
}

export interface ArtifactRef {
  name: string;
  uri: string;
}

// GET /api/executions/:executionId/stages/:stage/qc-summary
export interface QCSummary {
  executionId: string;
  stage: StageType;
  personaSlug?: string;
  rubricHash?: string;
  weightedScore?: number;
  subScores?: Record<string, number>;
  decision: 'pass' | 'borderline' | 'fail';
  reportRef?: string;
  updatedAt?: string;
}

// GET /api/executions/:executionId/heartbeats
export interface ExecutionHeartbeats {
  executionId: string;
  status: 'running' | 'completed' | 'failed' | 'paused';
  heartbeats: StageHeartbeat[];
}

export interface StageHeartbeat {
  stage: StageType;
  iteration: number;
  progress: number; // 0-100 percentage
  message: string;
  lastHeartbeat: string; // ISO timestamp
  isActive: boolean;
}

// API client interface
export interface ExecutionAPIClient {
  getSettings(executionId: string): Promise<ExecutionSettings>;
  getStageThresholds(executionId: string, stage: StageType): Promise<StageThresholds>;
  getStageIterations(executionId: string, stage: StageType): Promise<StageIterations>;
  getQCSummary(executionId: string, stage: StageType): Promise<QCSummary>;
  getHeartbeats(executionId: string): Promise<ExecutionHeartbeats>;
}

// TanStack Query key factories for consistent cache management
export const executionKeys = {
  all: ['executions'] as const,
  execution: (executionId: string) => ['exec', executionId] as const,
  settings: (executionId: string) => ['exec', executionId, 'settings'] as const,
  stageThresholds: (executionId: string, stage: StageType) => 
    ['exec', executionId, 'stage', stage, 'thresholds'] as const,
  stageIterations: (executionId: string, stage: StageType) => 
    ['exec', executionId, 'stage', stage, 'iterations'] as const,
  qcSummary: (executionId: string, stage: StageType) =>
    ['exec', executionId, 'stage', stage, 'qc-summary'] as const,
  heartbeats: (executionId: string) =>
    ['exec', executionId, 'heartbeats'] as const,
};

// Error types for API responses
export interface APIError {
  message: string;
  code?: string;
  details?: Record<string, any>;
}

export class APIClientError extends Error {
  constructor(
    message: string, 
    public status: number, 
    public code?: string,
    public details?: Record<string, any>
  ) {
    super(message);
    this.name = 'APIClientError';
  }
}